import { LayoutDashboard, Menu, PanelLeft, PanelTop } from 'lucide-react';

export type LayoutType = 'SIDEBAR' | 'TOPBAR' | 'MINIMAL' | 'DASHBOARD_FIRST';

interface LayoutSelectorProps {
  value: LayoutType;
  onChange: (layout: LayoutType) => void;
  disabled?: boolean;
}

const layouts: { type: LayoutType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    type: 'SIDEBAR',
    label: 'Sidebar',
    description: 'Menu lateral fixo à esquerda',
    icon: <PanelLeft className="w-6 h-6" />,
  },
  {
    type: 'TOPBAR',
    label: 'Topbar',
    description: 'Menu horizontal no topo',
    icon: <PanelTop className="w-6 h-6" />,
  },
  {
    type: 'MINIMAL',
    label: 'Minimal',
    description: 'Menu hamburger colapsável',
    icon: <Menu className="w-6 h-6" />,
  },
  {
    type: 'DASHBOARD_FIRST',
    label: 'Dashboard First',
    description: 'Dashboard como página inicial',
    icon: <LayoutDashboard className="w-6 h-6" />,
  },
];

export function LayoutSelector({ value, onChange, disabled }: LayoutSelectorProps) {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-white">Tipo de Layout</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {layouts.map((layout) => (
          <button
            key={layout.type}
            onClick={() => onChange(layout.type)}
            disabled={disabled}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              value === layout.type
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-surface-700 bg-surface-800 hover:border-surface-600'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div
              className={`mb-3 ${
                value === layout.type ? 'text-primary-400' : 'text-gray-400'
              }`}
            >
              {layout.icon}
            </div>
            <h4 className="font-medium text-white mb-1">{layout.label}</h4>
            <p className="text-sm text-gray-400">{layout.description}</p>
          </button>
        ))}
      </div>

      {/* Layout Preview */}
      <div className="mt-6 p-4 bg-surface-800 rounded-xl">
        <h4 className="text-sm font-medium text-gray-400 mb-3">Preview do Layout</h4>
        <div className="aspect-video bg-surface-900 rounded-lg overflow-hidden border border-surface-700">
          {value === 'SIDEBAR' && (
            <div className="flex h-full">
              <div className="w-16 bg-surface-800 border-r border-surface-700" />
              <div className="flex-1 p-2">
                <div className="h-8 bg-surface-800 rounded mb-2" />
                <div className="grid grid-cols-3 gap-2 h-20">
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                </div>
              </div>
            </div>
          )}
          {value === 'TOPBAR' && (
            <div className="flex flex-col h-full">
              <div className="h-10 bg-surface-800 border-b border-surface-700" />
              <div className="flex-1 p-2">
                <div className="grid grid-cols-3 gap-2 h-20">
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                </div>
              </div>
            </div>
          )}
          {value === 'MINIMAL' && (
            <div className="flex flex-col h-full">
              <div className="h-10 bg-surface-800 border-b border-surface-700 flex items-center px-3">
                <div className="w-6 h-6 bg-surface-700 rounded" />
              </div>
              <div className="flex-1 p-2">
                <div className="grid grid-cols-2 gap-2 h-20">
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                </div>
              </div>
            </div>
          )}
          {value === 'DASHBOARD_FIRST' && (
            <div className="flex h-full">
              <div className="w-12 bg-surface-800 border-r border-surface-700" />
              <div className="flex-1 p-2">
                <div className="grid grid-cols-4 gap-2 h-full">
                  <div className="col-span-2 row-span-2 bg-primary-900/30 rounded border border-primary-700/30" />
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                  <div className="bg-surface-800 rounded" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default LayoutSelector;
